import { useState } from 'react';
import type { AutomationRule } from '@hygge/shared';
import { useGenerateAutomation, type GenerateAutomationResult } from '@/hooks/use-automations';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function AiPromptBar({
  boardId,
  currentRule,
  onResult,
}: {
  boardId: string;
  currentRule?: AutomationRule;
  onResult: (res: GenerateAutomationResult) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const gen = useGenerateAutomation(boardId);
  const submit = () => {
    if (prompt.trim().length < 3) return;
    gen.mutate(
      { prompt: prompt.trim(), currentRule },
      {
        onSuccess: (res) => onResult(res),
        onError: () => toast.error("Couldn't generate the rule. Try rephrasing."),
      }
    );
  };
  return (
    <div className="rounded-lg border border-hygge/30 bg-hygge/5 p-3">
      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={currentRule ? 'Refine the rule below…' : 'Describe what to automate…'}
        className="min-h-[44px] text-sm"
        aria-label="Describe the automation"
      />
      <div className="mt-2 flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={submit}
          disabled={prompt.trim().length < 3 || gen.isPending}
          className="bg-cta text-cta-foreground hover:bg-cta/90"
        >
          {gen.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {gen.isPending ? 'Generating…' : 'Generate'}
        </Button>
      </div>
    </div>
  );
}
